import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { cardImageStyle } from "@/components/Card3D";
import { ABILITIES, MOVE_ICONS, AttributeChips, StatPip, statBoostSources, ArenaProfile, ResistGrid } from "@/components/ArenaBits";
import { ActionGrid, FighterLedger } from "@/components/CombatActions";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Swords, Shield, Flag, ArrowLeft, Trophy, Skull, Coins, Hourglass, Zap, Layers, Heart, Wrench, Ticket, Search, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { ChatPanel } from "@/components/ChatPanel";
import { LootRedeemPanel } from "@/components/LootRedeem";
import { LevelUpCelebration } from "@/components/LevelUpCelebration";
import { MessageSquare } from "lucide-react";

const statHpSources = (fighter) =>
  statBoostSources(fighter, "hp").map((s) => `• ${s.name} (${s.detail})`).join("\n");

const ABILITY_PERKS = [
  ["revive", (v) => `Revives once at ${Math.round(v * 100)}% HP`],
  ["regen", (v) => `Regenerates ${Math.round(v * 100)}% HP / turn`],
  ["counter", (v) => `Counters ${Math.round(v * 100)}% of melee damage`],
  ["thorns", (v) => `Thorns — reflects ${Math.round(v * 100)}% damage`],
  ["tough", (v) => `Takes ${Math.round(v * 100)}% less damage`],
  ["berserk", (v) => `Berserk — +${Math.round(v * 100)}% ATK below 50% HP`],
  ["last_stand", (v) => `Last Stand — +${Math.round(v * 100)}% DEF below 35% HP`],
  ["rage", (v) => `Rage — +${Math.round(v * 100)}% ATK per stack`],
  ["shield_pct", (v) => `Starts with a ${Math.round(v * 100)}% HP shield`],
];

// Convert a fighter's resist DICT (element→fraction) into the list ResistGrid wants.
const resistList = (r) =>
  r && !Array.isArray(r) ? Object.entries(r).map(([element, frac]) => ({ element, pct: Math.round((frac || 0) * 100) })) : (r || []);

// Full combat card sheet — every stat, trait, attack, ability, resistance & perk a
// fighter (including AI bosses / Machine soldiers / cavern monsters) carries.
function FighterSheet({ fighter, onClose }) {
  if (!fighter) return null;
  const f = fighter;
  const arena = {
    atk: f.atk, def: f.def, spd: f.spd, hp: f.max_hp, base: f.base, ability: f.ability,
    attributes: f.attributes || [], moves: f.moves || [], gear: f.gear || [], resist: resistList(f.resist),
  };
  const perks = ABILITY_PERKS.filter(([k]) => (f.mods || {})[k]).map(([k, fmt]) => fmt(f.mods[k]));
  const abilities = f.abilities_detail || (f.ability ? [{ id: f.ability, source: "NATURAL" }] : []);
  return (
    <Dialog open={!!fighter} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md bg-[hsl(222_22%_8%)] border-white/10 max-h-[90vh] overflow-y-auto" data-testid="fighter-sheet">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className={`relative w-12 ${f.orientation === "landscape" ? "aspect-[3.5/2.5]" : "aspect-[2.5/3.5]"} rounded overflow-hidden bg-black shrink-0 rarity-${f.rarity}`}>
              {f.front_image && <img src={f.front_image} alt={f.title} className="absolute inset-0 w-full h-full" style={cardImageStyle(f)} />}
              <div className="foil-overlay" />
            </div>
            <div className="min-w-0">
              <div className="font-display text-lg text-white truncate">{f.title}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{f.rarity}{f.level > 1 ? ` · Lv ${f.level}` : ""}{f.is_monster ? " · Boss" : ""}</div>
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Hp hp={f.hp} maxHp={f.max_hp} shield={f.shield || 0} maxShield={f.max_shield || 0} grievous={f.grievous || 0} />
          <ArenaProfile arena={arena} card={f} />
          {abilities.length > 0 && (
            <div>
              <div className="font-mono text-[8px] uppercase tracking-[0.3em] text-fuchsia-300/70 mb-1">Abilities</div>
              <div className="flex flex-wrap gap-1">
                {abilities.map((a, i) => {
                  const meta = ABILITIES[a.id];
                  return (
                    <span key={a.id} title={meta?.desc || a.id} data-testid={`sheet-ability-${a.id}`}
                      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono text-[10px] bg-fuchsia-400/10 border border-fuchsia-400/30 text-fuchsia-200">
                      {meta?.Icon && <meta.Icon className="h-2.5 w-2.5" />} {meta?.name || a.id}
                      <span className="text-fuchsia-300/50 text-[8px] uppercase">· {a.source}</span>
                    </span>
                  );
                })}
              </div>
            </div>
          )}
          {perks.length > 0 && (
            <div data-testid="sheet-perks">
              <div className="font-mono text-[8px] uppercase tracking-[0.3em] text-emerald-300/70 mb-1">Combat Perks</div>
              <ul className="space-y-0.5">
                {perks.map((p, i) => (
                  <li key={p} className="font-mono text-[10px] text-white/70 flex items-center gap-1.5"><Sparkles className="h-2.5 w-2.5 text-emerald-300 shrink-0" />{p}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


function Hp({ hp, maxHp, shield = 0, maxShield = 0, grievous = 0, slim = false }) {
  const pct = Math.max(0, Math.min(100, (hp / Math.max(1, maxHp)) * 100));
  const color = pct > 50 ? "bg-emerald-400" : pct > 25 ? "bg-amber-400" : "bg-red-500";
  // Shield tracks its OWN max so a small shield reads correctly; depletes from
  // the LEFT edge (blue clear bar shrinks in width as damage eats it).
  const sMax = Math.max(1, maxShield || shield || 1);
  const sPct = shield > 0 ? Math.max(0, Math.min(100, (shield / sMax) * 100)) : 0;
  // Grievous scaled to max_hp — how much of the next heal is negated.
  const gPct = grievous > 0 ? Math.max(0, Math.min(100, (grievous / Math.max(1, maxHp)) * 100)) : 0;
  const rowH = slim ? "h-1" : "h-1.5";
  return (
    <div className="w-full space-y-[2px]">
      {/* Anti-heal (grievous wound) lightning bar — TOP row when any is stacked */}
      {grievous > 0 && (
        <div className={cn("relative rounded-sm bg-fuchsia-900/25 border border-fuchsia-400/25 overflow-hidden", rowH)}
             data-testid="battle-antiheal-bar" title={`Grievous wound · ${grievous} HP of the next heal will be negated`}>
          <motion.div
            className="absolute inset-y-0 left-0"
            style={{ overflow: "hidden" }}
            animate={{ width: `${gPct}%` }}
            transition={{ duration: 0.45 }}
          >
            <svg width="100%" height="100%" viewBox="0 0 200 8" preserveAspectRatio="none" className="absolute inset-0">
              <rect x="0" y="0" width="200" height="8" fill="rgba(232,121,249,0.35)" />
              <motion.polyline
                fill="none" stroke="#f0abfc" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="miter"
                points="0,4 8,1 16,7 24,2 32,6 40,1 48,7 56,3 64,5 72,1 80,7 88,2 96,6 104,1 112,7 120,3 128,5 136,1 144,7 152,2 160,6 168,1 176,7 184,3 192,5 200,1"
                style={{ filter: "drop-shadow(0 0 3px #f472b6)" }}
                strokeDasharray="6 4"
                animate={{ strokeDashoffset: [-40, 0] }}
                transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
              />
            </svg>
          </motion.div>
        </div>
      )}
      {/* Shield — blue clear bar, own row, sits ABOVE the HP bar */}
      {(maxShield > 0 || shield > 0) && (
        <div className={cn("relative rounded-sm bg-sky-500/[0.08] border border-sky-300/25 overflow-hidden", rowH)}
             data-testid="battle-shield-bar" title={`Shield · ${shield}/${sMax} — absorbed 1:1, no resistances`}>
          <motion.div
            className="absolute inset-y-0 left-0"
            style={{
              background: "linear-gradient(90deg, rgba(125,211,252,0.85) 0%, rgba(56,189,248,0.55) 100%)",
              boxShadow: "inset 0 0 6px rgba(125,211,252,0.55)",
            }}
            animate={{ width: `${sPct}%` }} transition={{ duration: 0.45 }}
          />
        </div>
      )}
      {/* Main HP bar */}
      <div className={`relative ${slim ? "h-1" : "h-2"} rounded-full bg-black/70 border border-white/10 overflow-hidden`}>
        <motion.div className={`h-full ${color}`} animate={{ width: `${pct}%` }} transition={{ duration: 0.5 }} />
      </div>
      {!slim && (
        <div className="flex items-center justify-between font-mono text-[9px]">
          <span className="flex items-center gap-2">
            {shield > 0 && <span className="text-sky-300">⛨ {shield}</span>}
            {grievous > 0 && <span className="text-fuchsia-300">☇ {grievous}</span>}
          </span>
          <span className="text-white/60 tabular-nums">{hp} / {maxHp}</span>
        </div>
      )}
    </div>
  );
}

function ActiveFighter({ fighter, mirror = false, big = false, onInspect, belowCard = null }) {
  const ko = fighter.hp <= 0;
  const landscape = fighter.orientation === "landscape";
  return (
    <div className={cn("flex flex-col gap-2 w-full", big ? "max-w-[420px]" : "max-w-[360px]")}
      data-testid={`active-fighter-block-${fighter.card_id}`}>
      {/* Top row — portrait + vitals side by side */}
      <div className={cn("flex items-start gap-2.5", mirror && "flex-row-reverse")}>
      <motion.div
        key={fighter.card_id + ":" + fighter.hp}
        initial={{ x: mirror ? 6 : -6 }}
        animate={{ x: 0 }}
        transition={{ type: "spring", stiffness: 500, damping: 18 }}
        onClick={() => onInspect?.(fighter)}
        role="button"
        title="View full card"
        className={cn(
          "relative rounded-xl overflow-hidden card-aura bg-black shrink-0 cursor-pointer",
          big ? "w-28 sm:w-36" : "w-24 sm:w-28",
          landscape ? "aspect-[3.5/2.5]" : "aspect-[2.5/3.5]",
          `rarity-${fighter.rarity}`,
          ko && "grayscale opacity-50"
        )}
        data-testid={`active-fighter-${fighter.card_id}`}
      >
        {fighter.front_image && (
          <img src={fighter.front_image} alt={fighter.title} className="absolute inset-0 w-full h-full" style={cardImageStyle(fighter)} draggable={false} />
        )}
        <div className="foil-overlay" />
        <div className="absolute top-1.5 left-1.5 right-1.5 flex items-start justify-between gap-1">
          <div className="font-display text-[10px] text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)] truncate">{fighter.title}</div>
          {fighter.defending && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-cyan-400/90 text-black font-mono text-[8px] font-bold px-1 py-0.5 shrink-0"><Shield className="h-2.5 w-2.5" /> DEF</span>
          )}
        </div>
        {(fighter.active_conditions || []).length > 0 && !ko && (
          <div className="absolute bottom-1 left-1 right-1 flex flex-wrap gap-1" data-testid={`fighter-conditions-${fighter.card_id}`}>
            {fighter.active_conditions.map((c) => (
              <span key={c.key} data-testid={`fighter-condition-${c.key}-${fighter.card_id}`}
                className={cn(
                  "inline-flex items-center gap-0.5 rounded-full font-mono text-[7px] font-bold px-1 py-0.5 animate-pulse",
                  c.stat === "DEF"
                    ? "bg-cyan-400/90 text-black shadow-[0_0_10px_rgba(34,211,238,0.7)]"
                    : "bg-orange-500/90 text-black shadow-[0_0_10px_rgba(249,115,22,0.75)]"
                )}>
                {c.stat === "DEF" ? <Shield className="h-2 w-2" /> : <Swords className="h-2 w-2" />}
                {c.label} +{c.pct}% {c.stat}
              </span>
            ))}
          </div>
        )}
        {ko && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
            <span className="locked-stamp text-xl text-red-400">KO</span>
          </div>
        )}
      </motion.div>

      {/* stats — beside the card, not below */}
      <div className={cn("flex-1 min-w-0 flex flex-col gap-1 pt-0.5", mirror && "items-end text-right")}>
        <div className="w-full"><Hp hp={fighter.hp} maxHp={fighter.max_hp} shield={fighter.shield || 0} maxShield={fighter.max_shield || 0} grievous={fighter.grievous || 0} /></div>
        {fighter.shield > 0 && (
          <div className="font-mono text-[9px] text-cyan-300 inline-flex items-center gap-1" data-testid={`fighter-shield-${fighter.card_id}`}><Shield className="h-2.5 w-2.5" /> {fighter.shield} shield</div>
        )}
        {fighter.base?.hp != null && fighter.max_hp !== fighter.base.hp && (
          <div className={`font-mono text-[9px] inline-flex items-center gap-1 cursor-help ${fighter.max_hp > fighter.base.hp ? "text-green-400" : "text-red-400"}`}
            data-testid={`${fighter.max_hp > fighter.base.hp ? "fighter-hp-boosted" : "fighter-hp-reduced"}-${fighter.card_id}`}
            title={`MAX HP  ${fighter.base.hp} → ${fighter.max_hp}` + (statHpSources(fighter).length ? `\n${fighter.max_hp > fighter.base.hp ? "Boosted" : "Modified"} by:\n` + statHpSources(fighter) : "")}>
            <Heart className="h-2.5 w-2.5" /> {fighter.max_hp > fighter.base.hp ? "+" : ""}{fighter.max_hp - fighter.base.hp} max HP {fighter.max_hp > fighter.base.hp ? "▲" : "▼"}
          </div>
        )}
        <div className={cn("flex flex-col gap-1 font-mono text-[10px] text-white/60", mirror && "items-end")}>
          <StatPip fighter={fighter} statKey="atk" current={fighter.eff_atk ?? fighter.atk} Icon={Swords} color="text-orange-300" />
          <StatPip fighter={fighter} statKey="def" current={fighter.eff_def ?? fighter.def} Icon={Shield} color="text-cyan-300" />
          <StatPip fighter={fighter} statKey="spd" current={fighter.spd} Icon={Zap} color="text-amber-300" />
        </div>
      </div>
      </div>

      {/* Card-adjacent slot — the ME side drops its ActionGrid here so the moves
          sit directly under the portrait without hopping past the traits list. */}
      {belowCard}

      {/* Traits — full width, one per row, with each trait's numeric description */}
      <AttributeChips attributes={fighter.attributes} detailed className="w-full" />

      {/* Resistances — full width under the stats, no longer squeezed into the side column */}
      {fighter.resist && Object.keys(fighter.resist).length > 0 && (
        <ResistGrid resist={resistList(fighter.resist)} size="lg" className="w-full mt-1" />
      )}

      <button type="button" data-testid={`inspect-fighter-${fighter.card_id}`} onClick={() => onInspect?.(fighter)}
        className={cn("inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.15em] text-white/40 hover:text-cyan-300 transition-colors self-start", mirror && "self-end")}>
        <Search className="h-2.5 w-2.5" /> Full card
      </button>
    </div>
  );
}

function Bench({ player, activeIdx, clickable, onSwitch, onInspect, testidPrefix, align = "left" }) {
  return (
    <div className={cn("flex gap-1.5 flex-wrap", align === "right" ? "justify-end" : "justify-start")}>
      {player.squad.map((f, i) => {
        if (i === activeIdx) return null;
        const ko = f.hp <= 0;
        const canSwitch = clickable && !ko;
        return (
          <button
            key={f.card_id}
            type="button"
            onClick={() => (canSwitch ? onSwitch?.(i) : onInspect?.(f))}
            data-testid={`${testidPrefix}-bench-${i}`}
            title={canSwitch ? `Switch to ${f.title}` : `View ${f.title}`}
            className={cn(
              "w-12 sm:w-14 text-left rounded-lg overflow-hidden border transition-transform duration-150 cursor-pointer hover:scale-105",
              ko ? "border-white/5 opacity-50 grayscale hover:grayscale-0" : "border-white/15",
              canSwitch && "hover:border-cyan-400/60"
            )}
          >
            <div className={`relative ${f.orientation === "landscape" ? "aspect-[3.5/2.5]" : "aspect-[2.5/3.5]"} rarity-${f.rarity} bg-black`}>
              {f.front_image && <img src={f.front_image} alt={f.title} className="absolute inset-0 w-full h-full" style={cardImageStyle(f)} draggable={false} />}
              {ko && <div className="absolute inset-0 flex items-center justify-center bg-black/50"><Skull className="h-3.5 w-3.5 text-red-400" /></div>}
              <div className="absolute bottom-0.5 right-0.5 h-4 w-4 rounded-full bg-black/70 border border-white/20 flex items-center justify-center"><Search className="h-2 w-2 text-white/60" /></div>
            </div>
            <div className="p-0.5 bg-[hsl(220_18%_10%)]"><Hp hp={f.hp} maxHp={f.max_hp} shield={f.shield || 0} maxShield={f.max_shield || 0} grievous={f.grievous || 0} slim /></div>
          </button>
        );
      })}
    </div>
  );
}

const LOG_COLORS = { ko: "text-red-400", ability: "text-fuchsia-300", finish: "text-amber-300", start: "text-cyan-300", defend: "text-cyan-200", switch: "text-white/70", trait: "text-orange-300" };

export default function BattlePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const qc = useQueryClient();
  const [moving, setMoving] = useState(false);
  const [sheetFighter, setSheetFighter] = useState(null);
  const [surrendering, setSurrendering] = useState(false);
  // The "substitute-and-strike" chooser. When the player taps a bench card
  // on their turn, we pop this — they can either commit to a plain swap
  // (spending the turn), pick one of the INCOMING card's attacks (the sub
  // arrives AND strikes in the same action), or click out to cancel and keep
  // the original card active with its turn intact.
  const [switchPrompt, setSwitchPrompt] = useState(null);
  const logRef = useRef(null);

  const { data: battle } = useQuery({
    queryKey: ["battle", id],
    queryFn: async () => (await api.get(`/arena/battles/${id}`)).data,
    // The SSE stream below pushes a "wake up and refetch" ping the instant
    // either side moves; this interval is just a slow safety net in case a
    // ping is missed (e.g. brief network drop before EventSource reconnects).
    refetchInterval: (q) => {
      const b = q.state.data;
      if (!b) return 3000;
      if (b.status === "pending") return 8000;
      if (b.status !== "active") return false;
      return b.mode === "pvp" ? 10000 : false;
    },
  });

  // Live battle state — an SSE ping fires the instant either side saves a
  // move, so we refetch once immediately instead of polling every ~2.5s.
  useEffect(() => {
    if (!id) return;
    const es = new EventSource(`${API}/arena/battles/${id}/stream`, { withCredentials: true });
    es.onerror = () => { api.get("/auth/me").catch(() => es.close()); };
    es.onmessage = () => { qc.invalidateQueries({ queryKey: ["battle", id] }); };
    return () => es.close();
  }, [id, qc]);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [battle?.log?.length]);

  if (!battle) return <div className="max-w-6xl mx-auto px-4 py-12 text-center text-white/40">Entering the arena…</div>;

  const mySide = battle.my_side ?? 0;
  const me = battle.players[mySide];
  const opp = battle.players[1 - mySide];
  const myTurn = battle.status === "active" && battle.turn === mySide;
  const myActive = me.squad[me.active];
  const finished = battle.status === "finished";
  const iWon = finished && battle.winner_user_id === user?.id;
  const mustForfeit = finished && !iWon && battle.forfeit_mine && battle.forfeit_pending && (battle.forfeit_options || []).length > 0;
  const lootPending = finished && iWon && battle.loot_pending && Number(battle.loot_budget) > 0;
  const ability = myActive ? ABILITIES[myActive.ability] : null;

  const move = async (action, extra = {}) => {
    if (!myTurn || moving) return;
    setMoving(true);
    try {
      const r = await api.post(`/arena/battles/${id}/move`, { action, ...extra });
      qc.setQueryData(["battle", id], r.data);
      if (r.data.status === "finished") { refresh(); qc.invalidateQueries({ queryKey: ["arena-battles"] }); }
    } catch (e) { toast.error(apiError(e, "Move failed")); }
    finally { setMoving(false); }
  };

  const forfeit = async () => {
    if (!window.confirm("Forfeit this battle? Your opponent wins the pot.")) return;
    try {
      const r = await api.post(`/arena/battles/${id}/forfeit`);
      qc.setQueryData(["battle", id], r.data);
      refresh();
    } catch (e) { toast.error(apiError(e, "Failed")); }
  };

  const surrenderCard = async (cardId) => {
    setSurrendering(true);
    try {
      const r = await api.post(`/arena/battles/${id}/forfeit-card`, { card_id: cardId });
      qc.setQueryData(["battle", id], r.data);
      refresh();
      qc.invalidateQueries({ queryKey: ["my-cards"] });
      toast.success(r.data.forfeit_resolved_card?.locked_out ? "Card kept in your Playden, permanently locked out." : "Soldier surrendered to the shop.");
    } catch (e) { toast.error(apiError(e, "Failed to surrender card")); }
    finally { setSurrendering(false); }
  };

  // Pending PvP: waiting room
  if (battle.status === "pending") {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
        <Hourglass className="h-10 w-10 text-cyan-300 mx-auto animate-pulse" />
        <h1 className="font-display text-3xl text-white tracking-[0.08em] mt-4">WAITING FOR {opp.name?.toUpperCase()}</h1>
        <p className="text-white/50 mt-2 text-sm">Your challenge is pending. This page updates automatically when they accept.</p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <Button variant="outline" onClick={() => navigate("/arena")} className="border-white/15 text-white"><ArrowLeft className="h-4 w-4 mr-1" /> Back to Arena</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 sm:py-8" data-testid="battle-board">
      <LevelUpCelebration levelUps={finished ? battle.my_level_ups : null} />
      <div className="flex items-center justify-between mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate("/arena")} className="text-white/60" data-testid="battle-back-button">
          <ArrowLeft className="h-4 w-4 mr-1" /> Arena
        </Button>
        <div className="hud-pill rounded-full px-4 py-1.5 font-mono text-[11px] text-white/60 flex items-center gap-2" data-testid="battle-pot-pill">
          <Coins className="h-3.5 w-3.5 text-amber-300" />
          {battle.mode === "pvc"
            ? <>Wager: {battle.wager.toLocaleString()}c <span className="text-white/30">·</span> {battle.difficulty_name ? `${battle.difficulty_name} · ` : ""}win: wager back{battle.bonus_pct > 0 ? ` +${Math.round(battle.bonus_pct * 100)}%` : ""}{battle.card_rarity ? ` + ${battle.card_rarity} card` : ""}</>
            : <>Pot: {(battle.wager * 2).toLocaleString()}c <span className="text-white/30">·</span> House {Math.round((battle.fee_pct || 0) * 100)}%</>}
          {battle.stake_count > 0 && <><span className="text-white/30">·</span> <Layers className="h-3.5 w-3.5 text-violet-300" /> {battle.stake_count} cards</>}
        </div>
        {battle.status === "active" && (
          <Button variant="ghost" size="sm" onClick={forfeit} className="text-red-400/70 hover:text-red-300" data-testid="battle-forfeit-button">
            <Flag className="h-4 w-4 mr-1" /> Forfeit
          </Button>
        )}
        {finished && <div className="w-20" />}
      </div>

      {/* === BATTLE CHAT === Pinned at the top so it's always visible; the
         chat itself scrolls internally instead of the page scrolling out from
         under the battle stage below. */}
      <div className="mb-4 hud-pill rounded-2xl p-3" data-testid="battle-chat-section">
        <div className="flex items-center gap-2 mb-2">
          <MessageSquare className="h-3.5 w-3.5 text-cyan-300" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Battle Chat</span>
        </div>
        <ChatPanel channel={`battle:${id}`} testidPrefix="battle-chat" heightClass="h-36" />
      </div>

      {/* === HEAD-TO-HEAD STAGE === */}
      <FighterSheet fighter={sheetFighter} onClose={() => setSheetFighter(null)} />
      <div className="relative rounded-xl sm:rounded-2xl border border-white/10 bg-gradient-to-b from-rose-500/[0.05] via-transparent to-cyan-500/[0.06] p-2.5 sm:p-5 overflow-hidden" data-testid="battle-stage">
        {/* center seam */}
        <div className="pointer-events-none absolute inset-y-3 left-1/2 -translate-x-1/2 w-px bg-gradient-to-b from-transparent via-white/15 to-transparent" />

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-start gap-2 sm:gap-3">
          {/* OPPONENT (faces center) */}
          <div className="flex flex-col items-center sm:items-end gap-1.5 sm:gap-2.5 min-w-0">
            <div className="text-center sm:text-right">
              <div className="font-display text-base sm:text-xl text-white tracking-[0.05em] sm:tracking-[0.06em] truncate">{opp.name}</div>
              <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-rose-300/70">{opp.is_ai ? "AI Opponent" : "Collector"}</div>
            </div>
            {opp.squad[opp.active] && <ActiveFighter fighter={opp.squad[opp.active]} mirror big onInspect={setSheetFighter} />}
            <div className="self-center sm:self-start w-full">
              <div className="font-mono text-[8px] uppercase tracking-[0.25em] text-white/30 mb-0.5 sm:mb-1 text-center sm:text-left">Reserves</div>
              <div className="flex justify-center sm:justify-start"><Bench player={opp} activeIdx={opp.active} clickable={false} onInspect={setSheetFighter} testidPrefix="opp" align="left" /></div>
            </div>
          </div>

          {/* CENTER — VS + turn */}
          <div className="flex sm:flex-col items-center justify-center gap-2 sm:pt-10">
            <div className="relative h-9 w-9 sm:h-11 sm:w-11 rounded-full border border-white/15 bg-black/50 flex items-center justify-center shadow-[0_0_22px_rgba(255,255,255,0.08)]">
              <Swords className="h-4 w-4 sm:h-5 sm:w-5 text-white/70" />
            </div>
            {battle.status === "active" && (
              <div className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full text-center ${myTurn ? "bg-amber-300 text-black animate-pulse" : "bg-white/5 text-white/50"}`} data-testid="turn-indicator">
                {myTurn ? "YOUR TURN" : `${opp.name}'s turn…`}
              </div>
            )}
          </div>

          {/* ME (faces center) */}
          <div className="flex flex-col items-center sm:items-start gap-1.5 sm:gap-2.5 min-w-0">
            <div className="text-center sm:text-left">
              <div className="font-display text-base sm:text-xl text-white tracking-[0.05em] sm:tracking-[0.06em] truncate">{me.name} <span className="text-cyan-300 font-mono text-xs">(you)</span></div>
              <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-cyan-300/70">{myActive?.stun > 0 && myTurn ? "Stunned — turn lost" : myTurn ? "Choose your move" : "Standby"}</div>
            </div>
            {myActive && (
              <ActiveFighter
                fighter={myActive}
                big
                onInspect={setSheetFighter}
                belowCard={
                  battle.status === "active" && (
                    <div className="w-full" data-testid="battle-actions-inline">
                      {myActive?.hp <= 0 && myTurn && (
                        <div className="mb-2 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-rose-300 bg-rose-500/10 border border-rose-400/30 rounded-lg py-1.5" data-testid="downed-prompt">
                          Your fighter is down — pick a reserve to send out
                        </div>
                      )}
                      <div className="rounded-xl border border-white/10 bg-black/30 p-2 shadow-[inset_0_0_18px_rgba(0,229,255,0.06)]">
                        <ActionGrid
                          testidPrefix="battle"
                          stunned={myActive?.stun > 0}
                          disabled={!myTurn || myActive?.hp <= 0}
                          pending={moving}
                          moves={(myActive?.moves || []).map((m) => ({ ...m, current_cd: (myActive?.move_cds || {})[m.id] || 0 }))}
                          abilities={(myActive?.abilities_detail || []).map((d) => ({ id: d.id, source: d.source, current_cd: (myActive?.ability_cds || {})[d.id] || 0 }))}
                          interrupt={myActive?.can_interrupt ? { current_cd: myActive?.interrupt_cd || 0 } : null}
                          allies={(me.squad || []).map((f, i) => ({ key: i, label: f.title, hp: f.hp, maxHp: f.max_hp, alive: f.hp > 0 }))}
                          onAttack={(mid, allyKey) => move("attack", allyKey != null ? { move_id: mid, index: allyKey } : { move_id: mid })}
                          onAbility={(aid) => move("ability", { ability_id: aid })}
                          onDefend={() => move("defend")}
                          onInterrupt={() => move("interrupt")}
                        />
                      </div>
                    </div>
                  )
                }
              />
            )}
            <div className="self-center sm:self-end w-full">
              <div className="font-mono text-[8px] uppercase tracking-[0.25em] text-white/30 mb-1 text-center sm:text-right">Reserves {myTurn ? "· tap to switch" : ""}</div>
              <div className="flex justify-center sm:justify-end"><Bench player={me} activeIdx={me.active} clickable={myTurn && !moving} onSwitch={(i) => {
                // If your active fighter is DOWNED, you MUST send in a reserve —
                // no attack chooser, just swap in the next card.
                const activeKo = myActive?.hp <= 0;
                if (activeKo) return move("switch", { index: i });
                setSwitchPrompt({ index: i, fighter: me.squad[i] });
              }} onInspect={setSheetFighter} testidPrefix="my" align="right" /></div>
            </div>
          </div>
        </div>
      </div>

      {/* === BATTLE LOG === */}
      <div className="mt-4 hud-pill rounded-2xl p-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Battle Log</div>
        <div ref={logRef} className="max-h-32 overflow-y-auto space-y-1 pr-1" data-testid="battle-log">
          {battle.log.map((e) => (
            <div key={e.t} className={`text-xs font-mono ${e.crit ? "text-orange-300" : LOG_COLORS[e.type] || "text-white/60"}`}>
              <span className="text-white/25 mr-1.5">{String(e.t + 1).padStart(2, "0")}</span>{e.text}
            </div>
          ))}
        </div>
      </div>

      {/* Finished overlay */}
      <AnimatePresence>
        {switchPrompt && !finished && (
          <SubstitutePrompt
            fighter={switchPrompt.fighter}
            onCancel={() => setSwitchPrompt(null)}
            onSwapOnly={() => { const i = switchPrompt.index; setSwitchPrompt(null); move("switch", { index: i }); }}
            onSwapAttack={(mid) => { const i = switchPrompt.index; setSwitchPrompt(null); move("switch", { index: i, move_id: mid }); }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {finished && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <motion.div
              initial={{ scale: 0.8, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 20 }}
              className={`hud-pill rounded-2xl sm:rounded-3xl p-5 sm:p-10 text-center max-w-md w-full border ${iWon ? "border-amber-300/40" : "border-red-400/30"}`}
              data-testid="battle-result-overlay"
            >
              {iWon ? <Trophy className="h-14 w-14 text-amber-300 mx-auto" /> : <Skull className="h-14 w-14 text-red-400 mx-auto" />}
              <h2 className={`font-display text-2xl sm:text-4xl tracking-[0.08em] sm:tracking-[0.1em] mt-3 sm:mt-4 ${iWon ? "text-amber-300" : "text-red-400"}`}>
                {iWon ? "VICTORY" : "DEFEAT"}
              </h2>
              <p className="text-white/60 text-sm mt-2">
                {iWon
                  ? battle.reward > 0 ? `You won ${battle.reward.toLocaleString()} credits${battle.house_cut > 0 ? ` (house kept ${battle.house_cut.toLocaleString()}c)` : ""}.` : "Glory is its own reward."
                  : `${opp.name} takes the battle${battle.wager > 0 ? ` — and the pot` : ""}.`}
              </p>
              {iWon && battle.cards_won > 0 && (
                <p className="text-violet-300 text-sm mt-1 inline-flex items-center justify-center gap-1" data-testid="battle-cards-won">
                  <Layers className="h-4 w-4" /> + {battle.cards_won} card{battle.cards_won === 1 ? "" : "s"} claimed from the loser!
                </p>
              )}
              {!iWon && battle.stake_count > 0 && (
                <p className="text-red-300/80 text-sm mt-1" data-testid="battle-cards-lost">You lost {battle.stake_count} staked card{battle.stake_count === 1 ? "" : "s"}.</p>
              )}
              {iWon && battle.card_drop && (
                <div className="mt-4 flex flex-col items-center" data-testid="battle-card-drop">
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-300/80 mb-2">Victory Card</div>
                  <div className={`relative w-24 aspect-[2.5/3.5] rounded-lg overflow-hidden bg-black rarity-${battle.card_drop.rarity}`}>
                    {battle.card_drop.front_image && <img src={battle.card_drop.front_image} alt={battle.card_drop.title} className="absolute inset-0 w-full h-full object-cover" draggable={false} />}
                    <div className="foil-overlay" />
                  </div>
                  <div className="font-display text-sm text-white mt-2">{battle.card_drop.title}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/45 capitalize">{battle.card_drop.rarity}</div>
                </div>
              )}
              {iWon && battle.gear_drop && (
                <p className="text-emerald-300 text-sm mt-3 inline-flex items-center justify-center gap-1" data-testid="battle-gear-drop">
                  <Wrench className="h-4 w-4" /> Gear dropped: {battle.gear_drop.name}
                </p>
              )}
              {iWon && battle.arena_token_awarded > 0 && (
                <p className="text-cyan-300 text-sm mt-1 inline-flex items-center justify-center gap-1" data-testid="battle-token-awarded">
                  <Ticket className="h-4 w-4" /> + Arena Token earned
                </p>
              )}
              {mustForfeit ? (
                <div className="mt-5" data-testid="forfeit-panel">
                  <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-red-300 mb-1">Surrender a soldier</div>
                  <p data-testid="forfeit-rule-notice" className="text-white/55 text-xs mb-3">Choose one soldier to forfeit. Regular cards return to the shop; shared digital cards stay in your Playden, permanently locked out.</p>
                  <div className="grid grid-cols-3 gap-2">
                    {(battle.forfeit_options || []).map((c) => (
                      <button key={c.card_id} data-testid={`forfeit-option-${c.card_id}`} disabled={surrendering}
                        onClick={() => surrenderCard(c.card_id)}
                        className={`relative rounded-lg overflow-hidden border border-white/15 aspect-[2.5/3.5] bg-black hover:border-red-400 hover:ring-2 hover:ring-red-400/40 transition-all rarity-${c.rarity} disabled:opacity-50`}>
                        {c.front_image && <img src={c.front_image} alt={c.title} className="absolute inset-0 w-full h-full" style={cardImageStyle(c)} draggable={false} />}
                        <div className="foil-overlay" />
                        <div className="absolute inset-x-0 bottom-0 bg-black/75 px-1 py-0.5"><div className="font-mono text-[8px] text-white truncate">{c.title}</div>{c.locks_out && <span data-testid={`forfeit-lockout-${c.card_id}`} className="text-[8px] text-red-200">Permanent lockout</span>}</div>
                      </button>
                    ))}
                  </div>
                  <p className="text-white/30 text-[10px] mt-2">{surrendering ? "Surrendering…" : "You must choose a soldier to continue."}</p>
                </div>
              ) : lootPending ? (
                <LootRedeemPanel battleId={id} onClaimed={() => navigate("/arena")} />
              ) : (
                <div className="mt-6 flex items-center justify-center gap-3">
                  <Button onClick={() => navigate("/arena")} className="bg-cyan-400 text-black hover:bg-cyan-300" data-testid="battle-result-back">Back to Arena</Button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


// Substitute-and-strike chooser. Backdrop is dismissible — clicking anywhere
// outside the panel cancels the substitution and the original card keeps its
// turn intact. Inside, the incoming card's damage/support moves are laid out
// as icon-sized buttons so the player can BOTH swap AND strike in one action.
function SubstitutePrompt({ fighter, onCancel, onSwapOnly, onSwapAttack }) {
  const moves = (fighter?.moves || []).filter((m) => !m.kind || m.kind === "damage");
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4"
      onClick={onCancel}
      data-testid="substitute-prompt"
    >
      <motion.div
        initial={{ scale: 0.9, y: 8 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, opacity: 0 }}
        transition={{ type: "spring", stiffness: 320, damping: 22 }}
        onClick={(e) => e.stopPropagation()}
        className="hud-pill rounded-3xl p-5 max-w-lg w-full border border-cyan-400/40 bg-[hsl(220_18%_8%)]"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className={`relative h-16 w-12 rounded-md overflow-hidden bg-black shrink-0 rarity-${fighter.rarity}`}>
            {fighter.front_image && <img src={fighter.front_image} alt="" className="absolute inset-0 w-full h-full" style={cardImageStyle(fighter)} draggable={false} />}
            <div className="foil-overlay" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-300/80">Substitute in</div>
            <div className="font-display text-lg text-white tracking-[0.04em] truncate">{fighter.title}</div>
            <div className="font-mono text-[10px] text-white/50">Pick an opening strike — or swap only to save the move for next turn.</div>
          </div>
          <button data-testid="substitute-cancel" onClick={onCancel} className="text-white/45 hover:text-white text-xs font-mono uppercase tracking-wider">Cancel ×</button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {moves.length === 0 && (
            <div className="col-span-2 text-center text-white/45 text-xs py-3">This card has no damage moves — swap only.</div>
          )}
          {moves.map((m) => {
            const el = MOVE_ICONS[m.id];
            const Icon = el || Swords;
            const cd = 0; // reserves come in fresh, cd already decremented on entry
            return (
              <button
                key={m.id}
                data-testid={`substitute-strike-${m.id}`}
                onClick={() => onSwapAttack(m.id)}
                disabled={cd > 0}
                title={m.desc || m.name}
                className="rounded-lg border border-white/15 hover:border-amber-400/50 bg-black/40 hover:bg-amber-400/10 px-3 py-2 text-left transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-amber-300" />
                  <span className="font-mono text-[13px] text-white truncate">{m.name}</span>
                </div>
                <div className="font-mono text-[9px] text-white/45 mt-0.5">×{m.mult}{m.element ? ` · ${m.element}` : ""}</div>
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex items-center gap-2">
          <Button data-testid="substitute-swap-only" onClick={onSwapOnly} variant="outline" className="flex-1 border-white/15 text-white/80 hover:text-white">
            Swap only (no strike)
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}
